import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  FormControl,
  Select,
  Collapse,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  AccountBalance as AssetsIcon,
  Receipt as ExpensesIcon,
  Assessment as ReportsIcon,
  ShowChart as BloombergIcon,
  SwapHoriz as OperationsIcon,
  People as HoldersIcon,
  AccountBalanceWallet as AccountsIcon,
  Category as CategoriesIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  ExpandLess,
  ExpandMore,
  Logout as LogoutIcon,
  Person as PersonIcon,
  SupervisorAccount as BeneficiariesIcon,
  Public as JurisdictionsIcon,
  Home as RealEstateIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useApp } from '../App';

const DRAWER_WIDTH = 260;

const menuItems = [
  {
    group: 'Основные',
    items: [
      { path: '/dashboard', label: 'Дашборд', icon: <DashboardIcon /> },
      { path: '/assets', label: 'Активы', icon: <AssetsIcon /> },
      { path: '/operations', label: 'Операции', icon: <OperationsIcon /> },
    ],
  },
  {
    group: 'Учет',
    items: [
      { path: '/expenses', label: 'Расходы', icon: <ExpensesIcon /> },
      { path: '/reports', label: 'Отчеты', icon: <ReportsIcon /> },
    ],
  },
  {
    group: 'Данные',
    items: [
      { path: '/bloomberg', label: 'Bloomberg', icon: <BloombergIcon /> },
    ],
  },
  {
    group: 'Справочники',
    items: [
      { path: '/beneficiaries', label: 'Бенефициары', icon: <BeneficiariesIcon /> },
      { path: '/holders', label: 'Структуры', icon: <HoldersIcon /> },
      { path: '/jurisdictions', label: 'Юрисдикции', icon: <JurisdictionsIcon /> },
      { path: '/accounts', label: 'Счета', icon: <AccountsIcon /> },
      { path: '/real-estate', label: 'Недвижимость', icon: <RealEstateIcon /> },
      { path: '/businesses', label: 'Бизнесы', icon: <BusinessIcon /> },
    ],
  },
  {
    group: 'Система',
    items: [
      { path: '/settings', label: 'Настройки', icon: <SettingsIcon /> },
    ],
  },
];

const Layout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, workspace, setWorkspace } = useApp();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleGroupClick = (group) => {
    setExpandedGroup(expandedGroup === group ? null : group);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
    navigate('/login');
  };

  const currentDate = format(new Date(), 'dd MMMM yyyy', { locale: ru });

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'secondary.main' }}>
            BC
          </Typography>
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.2 }}>
            Branmore Capital
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Asset Management
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* Navigation */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', py: 1, px: 1 }}>
        {menuItems.map((group) => (
          <Box key={group.group} sx={{ mb: 1 }}>
            <ListItem
              sx={{
                py: 0.5,
                px: 1,
                cursor: 'pointer',
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' },
              }}
              onClick={() => handleGroupClick(group.group)}
            >
              <ListItemText
                primary={group.group}
                primaryTypographyProps={{
                  variant: 'overline',
                  color: 'text.secondary',
                  fontWeight: 600,
                }}
              />
              {expandedGroup === group.group ? <ExpandLess /> : <ExpandMore />}
            </ListItem>

            <Collapse in={expandedGroup === group.group || expandedGroup === null} timeout="auto">
              <List disablePadding>
                {group.items.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <ListItemButton
                      key={item.path}
                      selected={isActive}
                      onClick={() => handleNavigation(item.path)}
                      sx={{
                        ml: 1,
                        borderRadius: 1,
                        mb: 0.5,
                        '&.Mui-selected': {
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          },
                          '& .MuiListItemIcon-root': {
                            color: 'white',
                          },
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 40,
                          color: isActive ? 'white' : 'text.secondary',
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.label} />
                    </ListItemButton>
                  );
                })}
              </List>
            </Collapse>
          </Box>
        ))}
      </Box>

      <Divider />

      {/* User Info */}
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1,
            borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
            {user?.username?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={500} noWrap>
              {user?.username}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {user?.role}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  // Helper component for logo since AccountBalance is imported
  const AccountBalance = AssetsIcon;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          bgcolor: 'white',
          color: 'text.primary',
          boxShadow: 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" noWrap component="div" fontWeight={500}>
              Branmore Capital
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {currentDate}
            </Typography>
          </Box>

          {/* Workspace Selector */}
          <FormControl size="small" sx={{ minWidth: 180, mr: 2 }}>
            <Select
              value={workspace}
              onChange={(e) => setWorkspace(e.target.value)}
              sx={{ bgcolor: 'background.paper' }}
            >
              <MenuItem value="all">Все владельцы</MenuItem>
              <MenuItem value="ru">Только РФ активы</MenuItem>
              <MenuItem value="non-ru">Только не-РФ активы</MenuItem>
            </Select>
          </FormControl>

          {/* User Menu */}
          <IconButton onClick={handleUserMenuOpen} sx={{ p: 0 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              {user?.username?.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleUserMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleUserMenuClose}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              Профиль
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Выйти
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
          mt: 8,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
